import { Avatar, Button, Modal, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconUser } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useParentInsert } from '../../api/hooks/Parent/useParentInsert'
import styles from '../../styles/manager/GuardianInfoModal.module.scss'
import ModalInput from '../common/ModalInput'
import { useAuthStore } from '../../store'
import { Icon } from '../../assets/icons'
import { apiRequest } from '../../utils/api'
import { modals } from '@mantine/modals'
import SaveButton from '../common/SaveButton'
import { formatPhoneNumber } from '../../utils/utils'

const GuardianInfoModal = ({ opened, onClose, onSave, studentInfo }) => {
  const { user } = useAuthStore()
  const [formData, setFormData] = useState({
    NAME: '',
    EMAIL: '',
    PHONE: '',
    ADDR: '',
    USER_ID: '',
    PASSWORD: '',
    STATE: 'Y',
    COUNTRY: '',
    CITY: '',
    POSTCODE: '',
    CHILD_ID: studentInfo
  })
  const [countries, setCountries] = useState([])
  const [idAvailable, setIdAvailable] = useState(null)
  const parentInsertMutation = useParentInsert()

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }
  const handleSave = () => {
    if (
      !formData.NAME ||
      !formData.PHONE ||
      !formData.USER_ID ||
      !formData.PASSWORD
    ) {
      notifications.show({
        zIndex: 1002,
        title: 'Error',
        message: 'Please fill in required fields (Name, Phone, ID, Password)',
        color: 'red',
      })
      return
    }
    if (!isValidPassword(formData.PASSWORD)) {
      notifications.show({
        zIndex: 1002,
        title: 'Invalid password',
        message:
          'At least 8 characters with letters and numbers/special characters',
        color: 'red',
      })
      return
    }
    // API 데이터
    const apiData = {
      NAME: formData.NAME,
      EMAIL: formData.EMAIL,
      PHONE: formData.PHONE,
      ADDR: formData.ADDR,
      USER_ID: formData.USER_ID || formData.EMAIL,
      PASSWORD: formData.PASSWORD,
      STATE: formData.STATE,
      ID: studentInfo.id || 1,
      CHILD_ID: studentInfo
    }

    parentInsertMutation.mutate(apiData)
  }

  // React Query의 성공/실패 처리는 useEffect로 감지
  useEffect(() => {
    if (parentInsertMutation.isSuccess) {
      const insertedId = parentInsertMutation?.data?.insertedId || 0
      notifications.show({
        zIndex: 1002,
        title: 'Success',
        message: 'Guardian created successfully!',
        color: 'green',
      })

      onSave?.({
        id: insertedId,
        name: formData.NAME,
        email: formData.EMAIL,
        phone: formData.PHONE,
      })

      handleClose()
    }

    if (parentInsertMutation.isError) {
      console.error('Guardian creation failed:', parentInsertMutation.error)
      notifications.show({
        zIndex: 1002,
        title: 'Error',
        message: 'Failed to create guardian. Please try again.',
        color: 'red',
      })
    }
  }, [parentInsertMutation.isSuccess, parentInsertMutation.isError])

  const handleClose = () => {
    // 폼 초기화
    setFormData({
      NAME: '',
      EMAIL: '',
      PHONE: '',
      ADDR: '',
      USER_ID: '',
      PASSWORD: '',
      STATE: 'Y',
      COUNTRY: '',
      CITY: '',
      POSTCODE: '',
    })
    onClose()
  }

  const checkIdDuplicate = async () => {
    const userId = formData.USER_ID?.trim() || ''

    if (!userId) {
      showAlertModal('Please enter an ID to check for duplicates.')
      setIdAvailable(null)
      return
    }

    try {
      const res = await apiRequest('/checkId', 'POST', { ID: userId })
      setIdAvailable(res.available)
    } catch (err) {
      console.error('Failed to check ID duplication:', err)
      notifications.show({
        zIndex: 1002,
        title: 'Error',
        message: 'Invalid ID format.',
        color: 'red',
      })
      setIdAvailable(null)
    }
  }

  const isValidPassword = (pw) => {
    const lengthCheck = pw.length >= 8
    const letterCheck = /[a-zA-Z]/.test(pw)
    const numberOrSymbolCheck = /[\d\W]/.test(pw)
    return lengthCheck && letterCheck && numberOrSymbolCheck
  }

  const hasStudentInfo = studentInfo && Object.keys(studentInfo).length > 0

  const showAlertModal = (message) => {
    modals.open({
      zIndex: 1002,
      children: (
        <div className="alert-container">
          <img
            src="/assets/rabbit-blue-monster-cry.png"
            alt="avatar"
            className="rabbit-monster"
          />
          <p className="alert-text">{message}</p>
          <SaveButton
            onClick={() => modals.closeAll()}
            className="alert-button"
          >
            Confirm
          </SaveButton>
        </div>
      ),
      centered: true,
      withCloseButton: false,
    })
  }

  const handleSelectCountry = (value) => {
    setForm((prev) => ({
      ...prev,
      country: value,
    }))
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="xl"
      padding="xl"
      zIndex={1001}
      closeOnClickOutside={false}
      styles={{
        content: {
          borderRadius: '15px',
        },
        close: {
          top: '-20px',
          right: '10px',
        },
      }}
    >
      <p className={styles.title}>Guardian Information</p>

      <div className={styles.container}>
        <div className={styles.guardianWrapper}>
          <Avatar size={80} color="gray">
            <IconUser size={40} />
          </Avatar>
          <div className={styles.inputWrapper}>
            <p className={styles.textLg}>Guardian name</p>
            <ModalInput
              value={formData.NAME}
              onChange={(e) => handleInputChange('NAME', e.target.value)}
              placeholder="Enter guardian name"
              required
            />
            <p className={styles.textLg}>Insert email</p>
            <ModalInput
              value={formData.EMAIL}
              onChange={(e) => handleInputChange('EMAIL', e.target.value)}
              placeholder="guardian@example.com"
              type="email"
            />
          </div>
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionHeader}>
            <Icon name="info" size={22} />
            <p className={styles.textLg}>General information</p>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.fieldItem}>
              <p className={styles.textSm}>ID</p>
              <ModalInput
                value={formData.USER_ID}
                onChange={(e) => handleInputChange('USER_ID', e.target.value)}
                placeholder=""
                required
              />
              <SaveButton
                style={{ padding: '9px 4px' }}
                onClick={checkIdDuplicate}
              >
                Confirm
              </SaveButton>
              {idAvailable === false && (
                <p
                  style={{
                    color: 'red',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  This ID is already in use
                </p>
              )}
              {idAvailable === true && (
                <p
                  style={{
                    color: 'green',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  This ID is available
                </p>
              )}
            </div>

            <div className={styles.fieldItem}>
              <p className={styles.textSm}>Password</p>
              <ModalInput
                type="password"
                value={formData.PASSWORD}
                onChange={(e) => handleInputChange('PASSWORD', e.target.value)}
                placeholder=""
                required
              />
            </div>

            <div className={styles.fieldItem}>
              <p className={styles.textSm}>Phone</p>
              <ModalInput
                value={formData.PHONE}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value)
                  handleInputChange('PHONE', formatted)
                }}
                placeholder="000-0000-0000"
                required
              />
            </div>

            <div className={styles.divider}></div>

            {/*<div className={styles.fieldItem}>*/}
            {/*  <p className={styles.textSm}>Country</p>*/}
            {/*  <select*/}
            {/*    className={styles.selectInput}*/}
            {/*    value={formData.COUNTRY}*/}
            {/*    onChange={(e) => handleInputChange('COUNTRY', e.target.value)}*/}
            {/*  >*/}
            {/*    <option value="">Select Country</option>*/}
            {/*    {countries.map((country) => (*/}
            {/*      <option key={country.code} value={country.code}>*/}
            {/*        {country.name}*/}
            {/*      </option>*/}
            {/*    ))}*/}
            {/*  </select>*/}
            {/*</div>*/}

            {/*<div className={styles.fieldItem}>*/}
            {/*  <p className={styles.textSm}>City</p>*/}
            {/*  <ModalInput*/}
            {/*    value={formData.CITY}*/}
            {/*    onChange={(e) => handleInputChange('CITY', e.target.value)}*/}
            {/*    placeholder=""*/}
            {/*  />*/}
            {/*</div>*/}

            {/*<div className={styles.fieldItem}>*/}
            {/*  <p className={styles.textSm}>Postcode</p>*/}
            {/*  <ModalInput*/}
            {/*    value={formData.POSTCODE}*/}
            {/*    onChange={(e) => handleInputChange('POSTCODE', e.target.value)}*/}
            {/*    placeholder=""*/}
            {/*  />*/}
            {/*</div>*/}

            <div className={styles.fullWidthField}>
              <p className={styles.textSm}>Address</p>
              <ModalInput
                value={formData.ADDR}
                onChange={(e) => handleInputChange('ADDR', e.target.value)}
                placeholder=""
              />
            </div>
          </div>
        </div>

        <div className={styles.bottomSection}>
          {hasStudentInfo && (
            <div className={styles.studentSection}>
              <div className={styles.sectionHeader}>
                <Icon name="info" size={22} />
                <p className={styles.textLg}>Student info</p>
              </div>
              <div className={styles.studentInfoWrapper}>
                <Avatar size={60} color="gray">
                  <IconUser size={30} />
                </Avatar>
                <div className={styles.studentDetails}>
                  <Text>{studentInfo?.name || ''}</Text>
                  <Text size="sm" color="dimmed">
                    {studentInfo?.email || ''}
                  </Text>
                </div>
              </div>
            </div>
          )}

          <div className={styles.statusSection}>
            <div className={styles.sectionHeader}>
              <Icon name="info" size={22} />
              <p className={styles.textLg}>Status</p>
            </div>
            <div className={styles.buttonWrapper}>
              <Button
                variant={formData.STATE === 'Y' ? 'filled' : 'outline'}
                color="#97A4B4"
                onClick={() => handleInputChange('STATE', 'Y')}
                className={styles.statusButton}
              >
                Active
              </Button>
              <Button
                variant={formData.STATE === 'N' ? 'filled' : 'outline'}
                color="#97A4B4"
                onClick={() => handleInputChange('STATE', 'N')}
                className={styles.statusButton}
              >
                Inactive
              </Button>
            </div>
          </div>

        </div>

        <div className={styles.actionWrapper}>
          <Button
            variant="filled"
            onClick={handleSave}
            className={styles.saveButton}
            fullWidth
          >
            Save
          </Button>
          <Button
            variant="outline"
            onClick={handleClose}
            className={styles.cancelButton}
            fullWidth
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default GuardianInfoModal
